import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { X, Calendar, MapPin, Heart, ExternalLink, ChevronRight, Ticket, CalendarPlus, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import TicketPurchaseDialog from '../components/TicketPurchaseDialog';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cf82e0fe-468c-4a43-97a9-405e8da70dca/artifacts/znleb4a2_260207%20Logo%20Galeries%20Yess5zw.png";
const TICKET_IMAGE = "https://customer-assets.emergentagent.com/job_auction-app-4/artifacts/ppgvu2s3_Ticket4.png";
const CHARITY_IMAGE = "https://customer-assets.emergentagent.com/job_cf82e0fe-468c-4a43-97a9-405e8da70dca/artifacts/n3lkvhrq_Free%20a%20girl_Femke_cover_900.webp";
const STACKED_LOGO = "https://customer-assets.emergentagent.com/job_cf82e0fe-468c-4a43-97a9-405e8da70dca/artifacts/e9qtgtbr_260207%20Logo%20Galeries%20Yess4.png";

// Countdown Timer Component
const CountdownTimer = ({ targetDate, language }) => {
    const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

    useEffect(() => {
        const calculateTimeLeft = () => {
            const now = new Date();
            const target = new Date(targetDate);
            const difference = target - now;

            if (difference > 0) {
                setTimeLeft({
                    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                    minutes: Math.floor((difference / 1000 / 60) % 60),
                    seconds: Math.floor((difference / 1000) % 60),
                });
            } else {
                setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
            }
        };

        calculateTimeLeft();
        const timer = setInterval(calculateTimeLeft, 1000);
        return () => clearInterval(timer);
    }, [targetDate]);

    const labels = language === 'nl' 
        ? { days: 'dagen', hours: 'uren', minutes: 'min', seconds: 'sec' }
        : { days: 'days', hours: 'hours', minutes: 'min', seconds: 'sec' };

    return (
        <div className="grid grid-cols-4 gap-2 text-center">
            <div className="bg-black text-white p-3">
                <div className="text-2xl font-mono font-bold">{String(timeLeft.days).padStart(2, '0')}</div>
                <div className="text-[10px] uppercase tracking-wider opacity-70">{labels.days}</div>
            </div>
            <div className="bg-black text-white p-3">
                <div className="text-2xl font-mono font-bold">{String(timeLeft.hours).padStart(2, '0')}</div>
                <div className="text-[10px] uppercase tracking-wider opacity-70">{labels.hours}</div>
            </div>
            <div className="bg-black text-white p-3">
                <div className="text-2xl font-mono font-bold">{String(timeLeft.minutes).padStart(2, '0')}</div>
                <div className="text-[10px] uppercase tracking-wider opacity-70">{labels.minutes}</div>
            </div>
            <div className="bg-black text-white p-3">
                <div className="text-2xl font-mono font-bold">{String(timeLeft.seconds).padStart(2, '0')}</div>
                <div className="text-[10px] uppercase tracking-wider opacity-70">{labels.seconds}</div>
            </div>
        </div>
    );
};

// Add to Calendar helper
const generateCalendarLinks = (event) => {
    const startDate = new Date(event.start_datetime);
    const endDate = event.end_datetime ? new Date(event.end_datetime) : new Date(startDate.getTime() + 4 * 60 * 60 * 1000);
    
    const formatDateForGoogle = (date) => {
        return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    };
    
    const formatDateForICS = (date) => {
        return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    };

    const title = encodeURIComponent(event.title || 'Galeries Yess Event');
    const location = encodeURIComponent(event.location_name || event.address || '');
    const description = encodeURIComponent(`${event.title}\n\n${event.program_text || 'Curated charity auction salon'}`);

    // Google Calendar
    const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${formatDateForGoogle(startDate)}/${formatDateForGoogle(endDate)}&location=${location}&details=${description}`;

    // ICS file for Apple/Outlook
    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Galeries Yess//Event//EN
BEGIN:VEVENT
DTSTART:${formatDateForICS(startDate)}
DTEND:${formatDateForICS(endDate)}
SUMMARY:${event.title || 'Galeries Yess Event'}
LOCATION:${event.location_name || event.address || ''}
DESCRIPTION:${event.program_text || 'Curated charity auction salon'}
END:VEVENT
END:VCALENDAR`;

    const icsBlob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const icsUrl = URL.createObjectURL(icsBlob);

    return { googleUrl, icsUrl };
};

const MenuPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { api, user } = useAuth();
    const { t, language } = useLanguage();
    
    // Menu sections with translation keys
    const MENU_SECTIONS = [
        { id: 'auction-events', labelKey: 'auctionEvents' },
        { id: 'news', labelKey: 'news' },
        { id: 'charity', labelKey: 'charity' },
        { id: 'yess-items', labelKey: 'yessItems' },
        { id: 'about', labelKey: 'aboutUs' },
        { id: 'house-rules', labelKey: 'houseRules' },
    ];
    
    // Get section from URL or default to auction-events
    const sectionFromUrl = searchParams.get('section') || 'auction-events';
    const [activeSection, setActiveSection] = useState(sectionFromUrl);
    const [events, setEvents] = useState(null); // null = not loaded yet
    const [tickets, setTickets] = useState(null);
    const [items, setItems] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingMoreItems, setLoadingMoreItems] = useState(false);
    const [hasMoreItems, setHasMoreItems] = useState(true);
    const [eventTab, setEventTab] = useState('upcoming');
    const [dataFetched, setDataFetched] = useState(false);
    const [unreadNewsCount, setUnreadNewsCount] = useState(0);
    const [news, setNews] = useState([]);
    
    const ITEMS_PER_PAGE = 12;
    
    // Ticket purchase dialog state
    const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [purchasing, setPurchasing] = useState(false);
    
    // Dynamic content for About and House Rules
    const [aboutContent, setAboutContent] = useState(null);
    const [houseRulesContent, setHouseRulesContent] = useState(null);

    // Update active section when URL changes
    useEffect(() => {
        const section = searchParams.get('section');
        if (section && MENU_SECTIONS.some(s => s.id === section)) {
            setActiveSection(section);
        }
    }, [searchParams]);

    // Fetch data on mount and when returning to page
    useEffect(() => {
        fetchData();
        fetchUnreadNews();
        fetchNews();
    }, []);
    
    // Re-fetch data when page becomes visible again (after navigation)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                fetchData();
            }
        };
        
        const handleFocus = () => {
            fetchData();
        };
        
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);
        
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, []);
    
    // Re-fetch when switching to auction-events section
    useEffect(() => {
        if (activeSection === 'auction-events' && !loading) {
            fetchData();
        }
    }, [activeSection]);
    
    // Fetch dynamic content when about or house-rules section is selected
    useEffect(() => {
        if (activeSection === 'about' && !aboutContent) {
            fetchAboutContent();
        }
        if (activeSection === 'house-rules' && !houseRulesContent) {
            fetchHouseRulesContent();
        }
    }, [activeSection, aboutContent, houseRulesContent]);
    
    const fetchNews = async () => {
        try {
            const response = await api().get('/news/');
            setNews(response.data || []);
        } catch (error) {
            console.error('Failed to fetch news:', error);
        }
    };
    
    const fetchAboutContent = async () => {
        try {
            const response = await api().get('/content/about');
            setAboutContent(response.data);
        } catch (error) {
            console.error('Failed to fetch about content:', error);
        }
    };
    
    const fetchHouseRulesContent = async () => {
        try {
            const response = await api().get('/content/house-rules');
            setHouseRulesContent(response.data);
        } catch (error) {
            console.error('Failed to fetch house rules content:', error);
        }
    };

    const fetchUnreadNews = async () => {
        try {
            const response = await api().get('/news/unread-count');
            setUnreadNewsCount(response.data.unread_count || 0);
        } catch (error) {
            console.error('Failed to fetch unread news count:', error);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const [eventsRes, ticketsRes, itemsRes] = await Promise.all([
                api().get('/events/'),
                api().get('/tickets/my'),
                api().get(`/items/?limit=${ITEMS_PER_PAGE}`),
            ]);
            setEvents(eventsRes.data || []);
            setTickets((ticketsRes.data || []).filter(t => t.payment_status === 'paid'));
            const fetchedItems = itemsRes.data || [];
            setItems(fetchedItems);
            setHasMoreItems(fetchedItems.length === ITEMS_PER_PAGE);
            setDataFetched(true);
        } catch (error) {
            console.error('Failed to fetch data:', error);
            setEvents([]);
            setTickets([]);
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    const loadMoreItems = async () => {
        if (loadingMoreItems || !hasMoreItems) return;
        
        setLoadingMoreItems(true);
        try {
            const offset = items?.length || 0;
            const response = await api().get(`/items/?limit=${ITEMS_PER_PAGE}&offset=${offset}`);
            const newItems = response.data || [];
            setItems(prev => [...(prev || []), ...newItems]);
            setHasMoreItems(newItems.length === ITEMS_PER_PAGE);
        } catch (error) {
            console.error('Failed to load more items:', error);
        } finally {
            setLoadingMoreItems(false);
        }
    };

    const handleGetTicket = (event) => {
        setSelectedEvent(event);
        setPurchaseDialogOpen(true);
    };
    
    const handlePurchase = async (event, quantity) => {
        setPurchasing(true);
        try {
            const response = await api().post('/tickets/purchase', {
                event_id: event.id,
                origin_url: window.location.origin,
                quantity: quantity,
            });
            if (response.data.checkout_url) {
                window.location.href = response.data.checkout_url;
            } else if (response.data.ticket_ids || response.data.ticket_id) {
                const ticketCount = response.data.quantity || 1;
                toast.success(language === 'nl' 
                    ? `${ticketCount} gratis ticket(s) geclaimd!` 
                    : `${ticketCount} free ticket(s) claimed!`);
                if (response.data.mock_mode) {
                    toast.info('Payments are in test mode', { duration: 5000 });
                }
                setPurchaseDialogOpen(false);
                fetchData();
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to purchase ticket');
        } finally {
            setPurchasing(false);
        }
    };

    const hasTicket = (eventId) => (tickets || []).some(t => t.event_id === eventId);
    const upcomingEvents = (events || []).filter(e => e.status === 'upcoming' || e.status === 'live').slice(0, 1);
    const pastEvents = (events || []).filter(e => e.status === 'archived');

    return (
        <div className="min-h-screen bg-white pb-20">
            {/* Fixed Header with Logo and Close */}
            <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100 safe-area-top">
                <div className="flex items-center justify-center py-4 relative">
                    <img src={LOGO_URL} alt="Galeries Yess" className="h-8 w-auto" />
                    <button
                        onClick={() => navigate('/home')}
                        className="absolute right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
                        data-testid="menu-close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Section Tabs - Horizontally Scrollable */}
                <div className="flex gap-6 px-4 pb-4 overflow-x-auto scrollbar-hide">
                    {MENU_SECTIONS.map((section) => (
                        <button
                            key={section.id}
                            onClick={() => setActiveSection(section.id)}
                            className={`whitespace-nowrap text-sm tracking-wider uppercase transition-colors flex-shrink-0 pb-1 relative ${
                                activeSection === section.id
                                    ? 'text-black border-b-2 border-black font-medium'
                                    : 'text-gray-400 hover:text-gray-600'
                            }`}
                            data-testid={`menu-tab-${section.id}`}
                        >
                            {t(section.labelKey)}
                            {/* News notification badge */}
                            {section.id === 'news' && unreadNewsCount > 0 && (
                                <span className="absolute top-0 -right-1 w-2 h-2 bg-pink-500 rounded-full"></span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* Section Content - Scrollable */}
            <div className="pt-28 px-4 pb-24 overflow-y-auto">
                {/* AUCTION EVENTS */}
                {activeSection === 'auction-events' && (
                    <div className="animate-fade-in">
                        <h1 className="text-3xl md:text-4xl font-heading font-light tracking-tight mb-2">
                            {t('auctionEvents')}
                        </h1>
                        <p className="text-gray-500 mb-8">
                            {t('upcomingAuctions')}
                        </p>

                        {/* Upcoming/Past Tabs */}
                        <Tabs value={eventTab} onValueChange={setEventTab} className="mb-6">
                            <TabsList className="w-full bg-white p-1 border border-gray-200">
                                <TabsTrigger
                                    value="upcoming"
                                    className="flex-1 text-xs tracking-widest uppercase data-[state=active]:bg-black data-[state=active]:text-white"
                                >
                                    {t('upcoming')} ({upcomingEvents.length})
                                </TabsTrigger>
                                <TabsTrigger
                                    value="past"
                                    className="flex-1 text-xs tracking-widest uppercase data-[state=active]:bg-black data-[state=active]:text-white"
                                >
                                    {t('archived')} ({pastEvents.length})
                                </TabsTrigger>
                            </TabsList>

                            <TabsContent value="upcoming" className="mt-6">
                                {loading || events === null ? (
                                    <div className="flex flex-col items-center justify-center py-20">
                                        <div className="animate-pulse">
                                            <svg 
                                                className="w-10 h-10 text-pink-400" 
                                                fill="currentColor" 
                                                viewBox="0 0 24 24"
                                            >
                                                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                                            </svg>
                                        </div>
                                        <p className="text-xs text-gray-400 mt-3">Loading...</p>
                                    </div>
                                ) : upcomingEvents.length === 0 ? (
                                    <p className="text-gray-400 text-center py-12 italic">{t('noUpcomingEvents')}</p>
                                ) : (
                                    upcomingEvents.map((event) => {
                                        const userTicketCount = (tickets || []).filter(t => t.event_id === event.id).length;
                                        const userHasTicket = userTicketCount > 0;
                                        const calendarLinks = userHasTicket ? generateCalendarLinks(event) : null;
                                        const ticketsSold = event.tickets_sold || 0;
                                        const spotsLeft = (event.capacity || 50) - ticketsSold;
                                        
                                        // Ticket sale logic - only event status matters, not deadline
                                        const ticketSaleDeadline = event.ticket_sale_deadline ? new Date(event.ticket_sale_deadline) : null;
                                        const ticketSaleOpen = event.status === 'live';
                                        const ticketSaleClosed = event.status !== 'live';
                                        
                                        return (
                                        <div key={event.id} className="border border-gray-200 overflow-hidden mb-4">
                                            {/* Countdown Timer - Only show when event is LIVE and sale is open */}
                                            {event.status === 'live' && ticketSaleDeadline && !ticketSaleClosed && (
                                                <div className="p-4 bg-[#FCE7F0]">
                                                    <p className="text-xs uppercase tracking-wider text-center mb-3 font-medium">
                                                        {language === 'nl' ? 'Ticketverkoop sluit over' : 'Ticket sales close in'}
                                                    </p>
                                                    <CountdownTimer targetDate={ticketSaleDeadline} language={language} />
                                                    <p className="text-xs text-gray-500 text-center mt-3">
                                                        {language === 'nl' ? 'Tot 14 maart - 18:00' : 'Until March 14 - 18:00'}
                                                    </p>
                                                </div>
                                            )}
                                            
                                            {/* Ticket Sale Closed Banner */}
                                            {ticketSaleClosed && !userHasTicket && (
                                                <div className="p-4 bg-gray-100 text-center">
                                                    <p className="text-sm font-medium text-gray-600">
                                                        {language === 'nl' ? 'Ticketverkoop gesloten' : 'Ticket sale closed'}
                                                    </p>
                                                </div>
                                            )}
                                            
                                            <div className="relative aspect-square overflow-hidden">
                                                <img src={TICKET_IMAGE} alt={event.title} className="w-full h-full object-cover" />
                                                <div className="absolute top-3 right-3 flex items-center gap-2">
                                                    {event.status === 'live' && !ticketSaleClosed && (
                                                        <div className="px-3 py-1.5 bg-red-500 text-white text-xs font-mono uppercase animate-pulse">
                                                            LIVE
                                                        </div>
                                                    )}
                                                    <div className="px-3 py-1.5 bg-white/90 text-xs font-mono uppercase">
                                                        {event.season_tag || 'SS26'}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="p-6">
                                                <h3 className="text-xl font-heading font-medium mb-3">{event.title}</h3>
                                                {event.start_datetime && (
                                                    <div className="flex items-start gap-2 text-sm text-gray-500 mb-2">
                                                        <Calendar className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                                        <span className="leading-relaxed">
                                                            {format(new Date(event.start_datetime), 'EEEE, MMMM d, yyyy')} • {format(new Date(event.start_datetime), 'HH:mm')}
                                                            {event.end_datetime && ` - ${format(new Date(event.end_datetime), 'HH:mm')}`}
                                                        </span>
                                                    </div>
                                                )}
                                                {event.location_name && (
                                                    <div className="flex items-start gap-2 text-sm text-gray-500 mb-2">
                                                        <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                                        <span className="leading-relaxed">{event.location_name}</span>
                                                    </div>
                                                )}
                                                {/* Capacity info */}
                                                <div className="text-sm text-gray-400 mb-4">
                                                    {spotsLeft > 0 
                                                        ? `${spotsLeft} ${language === 'nl' ? 'plekken beschikbaar' : 'spots available'}`
                                                        : (language === 'nl' ? 'Uitverkocht' : 'Sold out')
                                                    }
                                                </div>
                                                
                                                {userHasTicket ? (
                                                    <div className="space-y-3">
                                                        <div className="flex items-center gap-2 text-green-600 py-2">
                                                            <Ticket className="w-5 h-5" />
                                                            <span className="font-medium">
                                                                {language === 'nl' 
                                                                    ? `Yess! Je hebt ${userTicketCount} ticket${userTicketCount > 1 ? 's' : ''}!` 
                                                                    : `Yess! You've got ${userTicketCount} ticket${userTicketCount > 1 ? 's' : ''}!`}
                                                            </span>
                                                        </div>
                                                        {/* Add to Calendar buttons */}
                                                        <div className="flex gap-2">
                                                            <a
                                                                href={calendarLinks?.googleUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="flex-1 flex items-center justify-center gap-2 py-2.5 border border-gray-200 text-sm hover:bg-gray-50 transition-colors"
                                                                data-testid="add-to-google-calendar"
                                                            >
                                                                <CalendarPlus className="w-4 h-4" />
                                                                Google
                                                            </a>
                                                            <a
                                                                href={calendarLinks?.icsUrl}
                                                                download={`galeries-yess-${event.season_tag || 'event'}.ics`}
                                                                className="flex-1 flex items-center justify-center gap-2 py-2.5 border border-gray-200 text-sm hover:bg-gray-50 transition-colors"
                                                                data-testid="add-to-apple-calendar"
                                                            >
                                                                <CalendarPlus className="w-4 h-4" />
                                                                Apple/Outlook
                                                            </a>
                                                        </div>
                                                        {/* Buy more tickets */}
                                                        {ticketSaleOpen && spotsLeft > 0 && (
                                                            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                                                                <span className="text-sm text-gray-500">
                                                                    {language === 'nl' ? 'Nog een ticket nodig?' : 'Need another ticket?'}
                                                                </span>
                                                                <Button
                                                                    onClick={() => handleGetTicket(event)}
                                                                    variant="outline"
                                                                    size="sm"
                                                                    className="rounded-full"
                                                                >
                                                                    + €{event.ticket_price?.toFixed(0) || '0'}
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                                                        <span className="text-2xl font-heading">€{event.ticket_price?.toFixed(0) || '0'}</span>
                                                        {ticketSaleClosed ? (
                                                            <span className="text-sm text-gray-400 italic">
                                                                {language === 'nl' ? 'Verkoop gesloten' : 'Sale closed'}
                                                            </span>
                                                        ) : spotsLeft <= 0 ? (
                                                            <Button
                                                                disabled
                                                                className="bg-gray-300 text-gray-500 rounded-full cursor-not-allowed"
                                                            >
                                                                {language === 'nl' ? 'Uitverkocht' : 'Sold out'}
                                                            </Button>
                                                        ) : ticketSaleOpen ? (
                                                            <Button
                                                                onClick={() => handleGetTicket(event)}
                                                                className="bg-[#F8BBD6] hover:bg-[#f0a8c8] text-black rounded-full px-6"
                                                            >
                                                                {language === 'nl' ? 'Koop Ticket' : 'Get Ticket'}
                                                            </Button>
                                                        ) : (
                                                            <span className="text-sm text-gray-400 italic">
                                                                {language === 'nl' ? 'Binnenkort beschikbaar' : 'Coming soon'}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        );
                                    })
                                )}
                            </TabsContent>

                            <TabsContent value="past" className="mt-6">
                                {pastEvents.length === 0 ? (
                                    <p className="text-gray-400 text-center py-12 italic">{t('noPastEvents')}</p>
                                ) : (
                                    pastEvents.map((event) => (
                                        <div key={event.id} className="border border-gray-100 p-4 mb-3 opacity-60">
                                            <h3 className="font-heading font-medium mb-1">{event.title}</h3>
                                            {event.start_datetime && (
                                                <p className="text-sm text-gray-400">{format(new Date(event.start_datetime), 'MMMM d, yyyy')}</p>
                                            )}
                                        </div>
                                    ))
                                )}
                            </TabsContent>
                        </Tabs>
                    </div>
                )}

                {/* NEWS */}
                {activeSection === 'news' && (
                    <div className="animate-fade-in">
                        <h1 className="text-3xl md:text-4xl font-heading font-light tracking-tight mb-4">
                            {t('news')}
                        </h1>
                        {/* Instagram follow link */}
                        <a 
                            href="https://www.instagram.com/galeriesyess" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-block text-base font-medium mb-8 hover:opacity-80 transition-opacity underline"
                            style={{ color: '#E91E8C' }}
                        >
                            Follow us @galeriesyess
                        </a>
                        
                        {/* Articles List - directly show articles */}
                        {news && news.length > 0 ? (
                            <div className="space-y-4">
                                {news.map((article) => (
                                    <button
                                        key={article.id}
                                        onClick={() => {
                                            // Mark as read
                                            try {
                                                api().post(`/news/${article.id}/read`);
                                            } catch (e) {}
                                            navigate(`/news/${article.id}`);
                                        }}
                                        className="w-full text-left group"
                                        data-testid={`article-${article.id}`}
                                    >
                                        <div className="flex gap-4 py-4 border-b border-gray-100">
                                            {article.header_image && (
                                                <div className="w-20 h-20 flex-shrink-0 overflow-hidden bg-gray-100">
                                                    <img 
                                                        src={article.header_image} 
                                                        alt={article.title}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <span className="text-xs text-pink-500 uppercase tracking-wider">
                                                    {article.category}
                                                </span>
                                                <h3 className="font-heading font-medium text-base mb-1 group-hover:text-gray-600 transition-colors line-clamp-2">
                                                    {article.title}
                                                </h3>
                                                <p className="text-xs text-gray-400">
                                                    {article.created_at && new Date(article.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                </p>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-400 text-center py-12 italic">
                                No articles yet
                            </p>
                        )}
                    </div>
                )}

                {/* CHARITY */}
                {activeSection === 'charity' && (
                    <div className="animate-fade-in">
                        <h1 className="text-3xl md:text-4xl font-heading font-light tracking-tight mb-2">
                            Charity
                        </h1>
                        <p className="text-gray-500 mb-8">
                            Fashion with purpose
                        </p>

                        <div className="aspect-square mb-8 overflow-hidden">
                            <img src={CHARITY_IMAGE} alt="Free a Girl" className="w-full h-full object-cover" />
                        </div>

                        <div className="space-y-6">
                            <div>
                                <h2 className="text-lg font-heading font-medium mb-2 flex items-center gap-2">
                                    <Heart className="w-5 h-5 text-pink-400" />
                                    Free a Girl
                                </h2>
                                <p className="text-sm text-gray-600">
                                    Galeries Yess proudly supports Free a Girl, fighting for a world without sexual exploitation of children.
                                </p>
                            </div>

                            <div>
                                <h2 className="text-lg font-heading font-medium mb-2">Their Mission</h2>
                                <div className="space-y-2 text-sm text-gray-600">
                                    <p><strong>Rescue</strong> — Liberating children from exploitation</p>
                                    <p><strong>Empower</strong> — Providing care, education & future</p>
                                    <p><strong>Justice</strong> — Ensuring perpetrators are prosecuted</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="text-center p-4 bg-gray-50">
                                    <span className="block text-2xl font-heading mb-1">8,500+</span>
                                    <span className="text-xs text-gray-500 uppercase">Children rescued</span>
                                </div>
                                <div className="text-center p-4 bg-gray-50">
                                    <span className="block text-2xl font-heading mb-1">14,000+</span>
                                    <span className="text-xs text-gray-500 uppercase">Cases reported</span>
                                </div>
                            </div>

                            <Button
                                onClick={() => window.open('https://www.freeagirl.nl/', '_blank')}
                                className="w-full h-12 bg-black hover:bg-black/90 text-white text-sm tracking-widest uppercase"
                            >
                                Learn more
                                <ExternalLink className="w-4 h-4 ml-2" />
                            </Button>
                        </div>
                    </div>
                )}

                {/* YESS ITEMS */}
                {activeSection === 'yess-items' && (
                    <div className="animate-fade-in">
                        <h1 className="text-3xl md:text-4xl font-heading font-light tracking-tight mb-2">
                            Yess Items
                        </h1>
                        <p className="text-gray-500 mb-8">
                            Browse curated auction pieces
                        </p>

                        {loading || items === null ? (
                            <div className="flex flex-col items-center justify-center py-16">
                                <div className="animate-pulse">
                                    <svg className="w-8 h-8 text-pink-400" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                                    </svg>
                                </div>
                                <p className="text-xs text-gray-400 mt-3">Loading...</p>
                            </div>
                        ) : (items || []).length === 0 ? (
                            <p className="text-gray-400 text-center py-12 italic">No items yet</p>
                        ) : (
                            <>
                                <div className="grid grid-cols-2 gap-4 mb-6">
                                    {(items || []).map((item) => (
                                        <div
                                            key={item.id}
                                            onClick={() => navigate(`/gallery/${item.id}`)}
                                            className="cursor-pointer group"
                                        >
                                            <div className="aspect-[3/4] overflow-hidden bg-gray-100 mb-2">
                                                <img
                                                    src={item.photo_processed || item.photos_original?.[0]}
                                                    alt={item.title}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                                />
                                            </div>
                                            <h3 className="text-sm font-medium truncate">{item.title}</h3>
                                            <p className="text-sm text-gray-500">€{item.price?.toFixed(0) || '0'}</p>
                                        </div>
                                    ))}
                                </div>
                                {hasMoreItems && (
                                    <Button
                                        onClick={loadMoreItems}
                                        disabled={loadingMoreItems}
                                        variant="outline"
                                        className="w-full"
                                    >
                                        {loadingMoreItems ? (
                                            <>
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                Loading...
                                            </>
                                        ) : (
                                            'Load more'
                                        )}
                                    </Button>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* ABOUT */}
                {activeSection === 'about' && (
                    <div className="animate-fade-in">
                        <h1 className="text-3xl md:text-4xl font-heading font-light tracking-tight mb-2">
                            About
                        </h1>
                        <p className="text-gray-500 mb-8">
                            The Galeries Yess story
                        </p>

                        {!aboutContent ? (
                            <div className="space-y-4">
                                <Skeleton className="h-48 w-48 mx-auto rounded-none" />
                                <Skeleton className="h-8 w-64 mx-auto" />
                                <Skeleton className="h-4 w-48 mx-auto" />
                                <Skeleton className="h-24 w-full" />
                            </div>
                        ) : (
                            <>
                                <div className="flex justify-center mb-12">
                                    <img src={STACKED_LOGO} alt="Galeries Yess" className="w-48 h-auto" />
                                </div>

                                <h2 className="text-2xl font-heading text-center mb-4">
                                    {aboutContent.tagline || "Women. Worth. Second Lives."}
                                </h2>
                                
                                <p className="text-sm text-gray-500 text-center mb-12 italic">
                                    {aboutContent.subtitle || "A curated charity auction salon"}
                                </p>

                                <div className="space-y-8 text-sm text-gray-600 leading-relaxed">
                                    {aboutContent.story_paragraphs && aboutContent.story_paragraphs.map((paragraph, index) => (
                                        <p key={index}>{paragraph}</p>
                                    ))}
                                </div>

                                <div className="mt-12 pt-8 border-t border-gray-100">
                                    <p className="text-xs text-gray-400 text-center uppercase tracking-widest mb-8">Curated by</p>
                                    <div className="flex justify-center gap-12">
                                        {aboutContent.founders && aboutContent.founders.map((founder, index) => (
                                            <div key={index} className="text-center">
                                                <div className="w-20 h-20 rounded-full overflow-hidden mb-3 mx-auto">
                                                    <img src={founder.photo_url} alt={founder.name} className="w-full h-full object-cover" />
                                                </div>
                                                <p className="text-sm font-heading">{founder.name}</p>
                                                {founder.role && (
                                                    <p className="text-xs text-gray-500">{founder.role}</p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Instagram */}
                                {aboutContent.instagram_url && (
                                    <div className="mt-8 text-center">
                                        <a 
                                            href={aboutContent.instagram_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-sm text-gray-500 hover:text-gray-700"
                                        >
                                            {aboutContent.instagram_handle}
                                        </a>
                                    </div>
                                )}

                                {/* Yeh Design Credit */}
                                {aboutContent.yeh_design_url && (
                                    <div className="mt-4 text-center">
                                        <a 
                                            href={aboutContent.yeh_design_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs text-gray-400 hover:text-gray-600"
                                        >
                                            {aboutContent.yeh_design_credit}
                                        </a>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* HOUSE RULES */}
                {activeSection === 'house-rules' && (
                    <div className="animate-fade-in">
                        <h1 className="text-3xl md:text-4xl font-heading font-light tracking-tight mb-2">
                            {t('houseRules')}
                        </h1>
                        <p className="text-gray-500 mb-8">
                            {houseRulesContent?.intro_text || t('houseRulesSubtitle')}
                        </p>

                        {!houseRulesContent ? (
                            <div className="space-y-4">
                                <Skeleton className="h-6 w-48" />
                                <Skeleton className="h-16 w-full" />
                                <Skeleton className="h-6 w-48" />
                                <Skeleton className="h-16 w-full" />
                                <Skeleton className="h-6 w-48" />
                                <Skeleton className="h-16 w-full" />
                            </div>
                        ) : (
                            <>
                                <div className="space-y-8 text-gray-600 leading-relaxed">
                                    {houseRulesContent.rules && houseRulesContent.rules.map((rule, index) => (
                                        <div key={index}>
                                            <h2 className="text-lg font-heading font-medium text-black mb-3">{rule.title}</h2>
                                            <p className="text-sm whitespace-pre-line">
                                                {rule.content}
                                            </p>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-12 pt-8 border-t border-gray-100">
                                    <p className="text-xs text-gray-400 text-center italic">
                                        {houseRulesContent.footer_text || t('thankYou')}
                                    </p>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Ticket Purchase Dialog */}
            <TicketPurchaseDialog
                open={purchaseDialogOpen}
                onOpenChange={setPurchaseDialogOpen}
                event={selectedEvent}
                onPurchase={handlePurchase}
                loading={purchasing}
            />
        </div>
    );
};

export default MenuPage;
